import { Clock, Zap } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useSharedTicker } from '../hooks/useSharedTicker';

interface Props {
  /** ISO timestamp at which the sweeper will auto-accept. Null ⇒ nothing renders. */
  autoAcceptAt: string | null | undefined;
  /** Set when the server has already auto-accepted (OrderResponse.autoAcceptedBySystem). */
  wasAutoAccepted?: boolean;
  className?: string;
}

const RAMP_RED_SECS = 10;
const RAMP_AMBER_SECS = 30;

/**
 * Countdown to the auto-accept window expiring. Does NOT call accept itself —
 * the backend sweeper is the authority. At zero, flips to "Auto-accepting…"
 * and waits for the incoming ORDER_ACCEPTED SSE to remove the parent card.
 */
export function AutoAcceptCountdown({ autoAcceptAt, wasAutoAccepted, className }: Props) {
  const now = useSharedTicker();

  if (wasAutoAccepted) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground',
          className
        )}
      >
        <Zap className="w-3 h-3" />
        Auto-accepted
      </span>
    );
  }

  if (!autoAcceptAt) return null;
  const target = Date.parse(autoAcceptAt);
  if (Number.isNaN(target)) return null;

  const secsRemaining = Math.max(0, Math.ceil((target - now) / 1000));

  if (secsRemaining <= 0) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground',
          className
        )}
      >
        <Clock className="w-3 h-3 animate-pulse" />
        Auto-accepting…
      </span>
    );
  }

  const mm = Math.floor(secsRemaining / 60)
    .toString()
    .padStart(2, '0');
  const ss = (secsRemaining % 60).toString().padStart(2, '0');

  const colorClass =
    secsRemaining <= RAMP_RED_SECS
      ? 'text-destructive'
      : secsRemaining <= RAMP_AMBER_SECS
        ? 'text-amber-500'
        : 'text-success';

  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium', colorClass, className)}>
      <Clock className="w-3 h-3" />
      Auto-accepts in {mm}:{ss}
    </span>
  );
}
